/* eslint-disable @typescript-eslint/no-explicit-any */
import DateInput from "@/components/inputs/DateInput";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import useRequest from "@/lib/request/useRequest";
import { ListRx, Rx } from "@/types/rx";
import { ManagementZones, Zone } from "@/types/managementZones";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { FeatureCollection, Polygon } from "geojson";
import { FertilizerProduct } from "@/types/products";
import { Pump } from "@/types/pump";
import SelectButtonInput from "@/components/inputs/SelectButtonInput";
import { ApplicationOperation } from "@/types/operations";
import { getPumpName } from "@/lib/utils/pump";
import dayjs from "dayjs";
import { Button, Divider } from "@heroui/react";
import AutoCompleteInput from "@/components/inputs/AutoCompleteInput";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { useNavigate } from "@tanstack/react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import ApplicationTable from "./ApplicationFormTable";
import {
    operationFormSchema,
    OperationFormType,
    degreesError,
    autoPopulateTable,
} from "@/lib/form/operation";
import ShapefileUpload from "./ShapefileUpload";
import { FileProperty } from "@/types/file";
import TextAreaInput from "@/components/inputs/TextAreaInput";
import { useTranslation } from "react-i18next";
import { ApplicationMethod, RxType } from "@/types/enums";
import ProductAutoComplete from "@/components/inputs/ProductAutoComplete";
import { hasFileSource } from "@/lib/utils/operations";
import useMap from "@/lib/map/useMap";
import {
    SUMMARY_OPERATION_IMAGE_LAYER,
    SUMMARY_OPERATIONS_SOURCE,
} from "@/lib/map/layers";
import {
    removeLayersIfExists,
    removeSourcesIfExists,
    invertedTurfBboxToFourCornerCoordinates,
    Coordinates,
} from "@/lib/utils/map";
import { ImageSource } from "mapbox-gl";

function ApplicationOperationForm({
    existingApplicationId,
}: {
    existingApplicationId?: number;
}) {
    const { t } = useTranslation("operations");
    const { fieldId, fieldQuery, fieldBbox } = useFieldDashboard() ?? {};
    const { useGetRequest, useMutateRequest } = useRequest();
    const navigate = useNavigate();
    const { map, mapLoaded } = useMap();

    const existingApplicationQuery = useGetRequest<ApplicationOperation>({
        url: `application_operations/${existingApplicationId}/`,
        enabled: !!existingApplicationId,
    });

    const ratesParam = useMemo(() => {
        let param;
        if (existingApplicationQuery.data?.prescription) {
            param = "prescription";
        } else if (
            existingApplicationQuery.data?.method ===
            ApplicationMethod.Fertigation
        ) {
            param = "sectors";
        } else {
            param = "irrigated";
        }
        return param;
    }, [
        existingApplicationQuery.data?.method,
        existingApplicationQuery.data?.prescription,
    ]);

    const mapImageQuery = useGetRequest({
        url: `application_operations/${existingApplicationId}/map_display_image/`,
        responseType: "blob",
        enabled: !!existingApplicationId,
    });

    const existingRatesQuery = useGetRequest<
        {
            mean: number;
            start: number;
            stop: number;
            zone_number: number;
            irrigated: boolean;
            sector: number;
            treatment: string;
        }[]
    >({
        url: `application_operations/${existingApplicationId}/rates/`,
        params: { by: ratesParam },
        enabled: !!existingApplicationId && !!ratesParam,
    });
    const irrigationRatesQuery = useGetRequest<
        {
            mean: number;
            start: number;
            stop: number;
        }[]
    >({
        url: `irrigation_operations/${existingApplicationQuery.data?.irrigation_operation?.id}/rates/`,
        params: { by: "sectors" },
        enabled: !!existingApplicationQuery.data?.irrigation_operation?.id,
    });

    const methods = useForm<OperationFormType>({
        defaultValues: {
            prescription: null,
            is_complete: "true" as unknown as boolean,
            product:
                fieldQuery?.data?.default_product?.toString() as unknown as number,
            _skipTableValidation: false,
        } as any,
        resolver: yupResolver(operationFormSchema),
        mode: "all",
    });

    const { handleSubmit, setValue, watch, getValues, trigger } = methods;

    useEffect(() => {
        if (!map || !mapLoaded || !fieldBbox || !existingApplicationId) return;

        const imageUrl = !mapImageQuery.data
            ? null
            : window.URL.createObjectURL(mapImageQuery.data as Blob);
        const bbox = existingApplicationQuery.data?.bounds
            ? (invertedTurfBboxToFourCornerCoordinates(
                existingApplicationQuery.data.bounds,
            ) as Coordinates)
            : undefined;

        if (imageUrl) {
            const imSource = map.getSource<ImageSource>(
                SUMMARY_OPERATIONS_SOURCE,
            );
            if (imSource) {
                if (imSource.url !== imageUrl) {
                    imSource.updateImage({
                        url: imageUrl,
                        coordinates: bbox || fieldBbox,
                    });
                }
            } else {
                map.addSource(SUMMARY_OPERATIONS_SOURCE, {
                    type: "image",
                    url: imageUrl,
                    coordinates: bbox || fieldBbox,
                });
                map.addLayer(SUMMARY_OPERATION_IMAGE_LAYER);
            }
        } else {
            removeLayersIfExists(map, [SUMMARY_OPERATION_IMAGE_LAYER.id]);
            removeSourcesIfExists(map, [SUMMARY_OPERATIONS_SOURCE]);
        }

        return () => {
            removeLayersIfExists(map, [SUMMARY_OPERATION_IMAGE_LAYER.id]);
            removeSourcesIfExists(map, [SUMMARY_OPERATIONS_SOURCE]);
        };
    }, [
        map,
        mapLoaded,
        fieldBbox,
        existingApplicationId,
        mapImageQuery.data,
        existingApplicationQuery.data?.bounds,
    ]);

    useEffect(() => {
        if (existingApplicationQuery.data) {
            setValue(
                "start_date",
                dayjs(existingApplicationQuery.data.start_date).toDate(),
            );
            setValue(
                "product",
                existingApplicationQuery.data.application_products[0]?.product?.id?.toString() as any,
            );
            setValue(
                "is_complete",
                existingApplicationQuery.data.is_complete.toString() as unknown as boolean,
            );
            setValue(
                "prescription",
                (
                    existingApplicationQuery.data.prescription as any
                )?.id?.toString() as any,
            );
            setValue("method", existingApplicationQuery.data.method);
            setValue("note", existingApplicationQuery.data.note);
        }
    }, [existingApplicationQuery.data, setValue]);

    const [prescription, method, template, product, table] = watch([
        "prescription",
        "method",
        "template",
        "product",
        "table",
    ]);

    const hasSectors = useMemo(() => {
        return !!table?.some((row) => row.sector != null);
    }, [table]);

    const prescriptionQuery = useGetRequest<ListRx[]>({
        url: `fields/${fieldId}/prescriptions/`,
    });

    const selectedPrescriptionQuery = useGetRequest<Rx>({
        url: `prescriptions/${prescription}/`,
        enabled: !!prescription,
    });

    useEffect(() => {
        if (selectedPrescriptionQuery?.data && !existingApplicationId) {
            if (selectedPrescriptionQuery?.data?.rx_type !== RxType.ZONE) {
                setValue("method", ApplicationMethod.Fertigation);
            }
            setValue(
                "product",
                selectedPrescriptionQuery?.data?.fertilizer_product?.toString() as any,
            );
            setValue(
                "table",
                selectedPrescriptionQuery?.data?.table?.features.map(
                    (feature) => ({
                        ...feature.properties,
                    }),
                ) ?? [],
            );
        }
    }, [setValue, selectedPrescriptionQuery?.data, existingApplicationId]);

    const managementZonesQuery = useGetRequest<ManagementZones[]>({
        url: `fields/${fieldId}/management_zones/`,
    });

    const irrigatedClipQuery = useGetRequest<FeatureCollection<Polygon, Zone>>({
        url: `fields/${fieldId}/irrigated_clip/`,
    });

    const managementZoneQuery = useGetRequest<FeatureCollection<Polygon, Zone>>(
        {
            url: `management_zones/${template}/irrigated_clip/`,
            enabled: !!template,
        },
    );

    const productsQuery = useGetRequest<FertilizerProduct[]>({
        url: `fertilizers/`,
    });

    const selectedProduct = useMemo(() => {
        return productsQuery.data?.find(
            (fertilizer) => fertilizer.id == product,
        );
    }, [productsQuery.data, product]);

    const formatTable = useCallback(
        (
            table?: {
                target_rate_n?: number;
                product_rate?: number;
                speed?: number;
                depth?: number;
            }[],
        ) => {
            return autoPopulateTable(
                table,
                selectedProduct,
                fieldQuery?.data?.irrigation_systems[0],
            );
        },
        [selectedProduct, fieldQuery?.data?.irrigation_systems],
    );
    const setTableDefault = useCallback(
        (method: ApplicationMethod) => {
            if (method === ApplicationMethod.Fertigation) {
                setValue(
                    "table",
                    formatTable(
                        irrigatedClipQuery.data?.features
                            ?.filter((feature) => feature.properties.irrigated)
                            .map((feature) => ({
                                ...feature.properties,
                                target_rate_n: fieldQuery?.data?.default_n_rate,
                                speed: 100,
                                depth: fieldQuery?.data?.default_depth,
                            })),
                    ),
                );
            } else {
                setValue(
                    "table",
                    formatTable(
                        irrigatedClipQuery.data?.features.map((feature) => ({
                            ...feature.properties,
                            target_rate_n: fieldQuery?.data?.default_n_rate,
                        })),
                    ),
                );
            }
        },
        [
            setValue,
            irrigatedClipQuery.data?.features,
            fieldQuery?.data?.default_n_rate,
            fieldQuery?.data?.default_depth,
            formatTable,
        ],
    );
    const pumpsQuery = useGetRequest<Pump[]>({
        url: `pumps/`,
    });

    const saveOperation = useMutateRequest<{ id: number }>({
        errorMessage: t("error.failed_to_save_operation"),
        successMessage: t("success.operation_saved_successfully"),
    });

    const uploadOperation = useMutateRequest({
        errorMessage: t("error.failed_to_upload_operation"),
        successMessage: t("success.operation_uploaded_successfully"),
    });

    const degreesErrorMessage = hasFileSource(existingApplicationQuery.data)
        ? undefined
        : degreesError({
            table,
            method,
            irrigationSystem: fieldQuery?.data?.irrigation_systems[0],
            hasSectors,
        });
    const previousTableRef = useRef<OperationFormType["table"]>([]);

    const onSubmit = (formData: OperationFormType) => {
        if (degreesErrorMessage) {
            return;
        }
        const data: OperationFormType & {
            [x: string]: any;
        } = {
            ...formData,
            field: fieldId,
            operation_type: "fertilizer",
            application_products: [{ price: null, product }],
            start_date: dayjs(formData.start_date).format(
                "YYYY-MM-DD",
            ) as unknown as Date,
            end_date: null,
            provider: existingApplicationQuery.data?.provider ?? null,
            source_id: existingApplicationQuery.data?.source_id ?? null,
            average_rate: null,
            note: formData.note === "" ? null : formData.note ?? null,
            table: formData?.table?.length
                ? formData.table
                : previousTableRef.current,
        };

        const shouldSendRates =
            !selectedFile &&
            !hasFileSource(existingApplicationQuery.data);

        if (shouldSendRates) {
            if (data.prescription) {
                data["prescription_rates"] = data.table?.map((feature, index) => ({
                    row: index,
                    rate: feature.target_rate_n,
                }));
            }

            if (
                !data.prescription &&
                !data.template &&
                data.method !== ApplicationMethod.Fertigation
            ) {
                data["irrigated_rates"] = data.table?.map((feature) => ({
                    irrigated: feature.irrigated,
                    rate: feature.target_rate_n,
                }));
            }
            if (
                !data.prescription &&
                data.template &&
                data.technique !== ApplicationMethod.Fertigation
            ) {
                data["zone_rates"] = data.table?.map((feature) => ({
                    zone: feature.id,
                    irrigated: feature.irrigated,
                    rate: feature.target_rate_n,
                }));
            }

            if (data.method === ApplicationMethod.Fertigation) {
                data["irrigation_operation"] = {
                    id: existingApplicationQuery.data?.irrigation_operation?.id,
                    irrigation_system: fieldQuery?.data?.irrigation_systems[0]?.id,
                    field: fieldId,
                    start_date: dayjs(data.start_date).format("YYYY-MM-DD"),
                    end_date: null,
                    provider: null,
                    source_id: null,
                    note: "irrigation",
                    sector_rates: data.table?.map((feature) => ({
                        start: feature.start,
                        stop: feature.stop,
                        rate: feature.depth,
                    })),
                };
            }

            if (
                data.method === ApplicationMethod.Fertigation &&
                !data.prescription
            ) {
                data["sector_rates"] = data.table?.map((feature) => ({
                    start: feature.start,
                    stop: feature.stop,
                    rate: feature.target_rate_n,
                }));
            }
        }

        saveOperation.mutate(
            {
                data,
                method: existingApplicationId ? "PUT" : "POST",
                url: existingApplicationId
                    ? `application_operations/${existingApplicationId}/`
                    : `application_operations/`,
            },
            {
                onSuccess: (data) => {
                    if (selectedFile) {
                        const fileUpload = new FormData();
                        fileUpload.append("file", selectedFile);
                        fileUpload.append(
                            "properties",
                            JSON.stringify(getValues("properties")),
                        );
                        uploadOperation.mutate(
                            {
                                data: fileUpload,
                                url: `application_operations/${data.id}/uploadV2/`,
                                method: "put",
                            },
                            {
                                onSuccess: () => {
                                    navigate({
                                        to: "/dashboard/fields/$fieldId/operations",
                                        params: {
                                            fieldId: fieldId as string,
                                        },
                                    });
                                },
                            },
                        );
                    } else {
                        navigate({
                            to: "/dashboard/fields/$fieldId/operations",
                            params: {
                                fieldId: fieldId as string,
                            },
                        });
                    }
                },
            },
        );
    };

    const rxOptions = useMemo(() => {
        return prescriptionQuery.data?.map((rx) => ({
            id: rx.id,
            description: rx.name,
        }));
    }, [prescriptionQuery.data]);

    const pumpOptions = useMemo(() => {
        return pumpsQuery.data
            ?.filter((pump) => pump.grower == fieldQuery?.data?.grower)
            .map((pump, index) => ({
                id: pump.id,
                description: getPumpName(pump, index),
            }));
    }, [pumpsQuery.data, fieldQuery?.data?.grower]);

    const methodOptions = useMemo(() => {
        const types = [
            {
                id: ApplicationMethod.InjectedKnifedCoultered,
                description: "Injected/Knifed/Coultered",
            },
            { id: ApplicationMethod.Dribble, description: "Dribble" },
            { id: ApplicationMethod.Broadcast, description: "Broadcast" },
            { id: ApplicationMethod.Topdress, description: "Topdress" },
            { id: ApplicationMethod.YDrop, description: "Y-Drop" },
            {
                id: ApplicationMethod.BroadcastTillage,
                description: "Broadcast & Incorporated",
            },
            { id: ApplicationMethod.TwoXTwo, description: "2x2" },
            { id: ApplicationMethod.Furrow, description: "In-Furrow" },
            { id: ApplicationMethod.Fertigation, description: "Fertigation" },
        ];

        return sortArrayByKey(types, "description");
    }, []);

    const isFertigation = useMemo(() => {
        return method === ApplicationMethod.Fertigation;
    }, [method]);

    const managementZoneOptions = useMemo(() => {
        return managementZonesQuery.data?.map((managementZone) => ({
            id: managementZone.id,
            description: managementZone.name,
        }));
    }, [managementZonesQuery.data]);

    useEffect(() => {
        if (template && managementZoneQuery.data) {
            setValue(
                "table",
                formatTable(
                    managementZoneQuery.data.features.map((feature) => ({
                        ...feature.properties,
                        target_rate_n: fieldQuery?.data?.default_n_rate,
                    })),
                ),
            );
            setValue("prescription", undefined);
        }
    }, [
        setValue,
        fieldQuery?.data?.default_n_rate,
        formatTable,
        managementZoneQuery.data,
        template,
    ]);

    useEffect(() => {
        if (method && !prescription && !template) {
            setTableDefault(method);
        }
    }, [method, prescription, setTableDefault, template]);

    useEffect(() => {
        if (existingRatesQuery.data) {
            setValue(
                "table",
                formatTable(
                    existingRatesQuery.data?.map((rate) => ({
                        target_rate_n: rate.mean,
                        start: rate.start,
                        stop: rate.stop,
                        zone_number: rate.zone_number,
                        irrigated: rate.irrigated,
                        sector: rate.sector,
                        treatment: rate.treatment,
                    })),
                ),
            );
        }
    }, [existingRatesQuery.data, setValue, formatTable]);

    useEffect(() => {
        if (irrigationRatesQuery.data) {
            const table = getValues("table");
            if (!table) return;
            setValue(
                "table",
                formatTable(
                    table.map((row, index) => {
                        return {
                            ...row,
                            depth: irrigationRatesQuery.data[index]?.mean,
                        };
                    }),
                ),
            );
        }
    }, [irrigationRatesQuery.data, getValues, formatTable, setValue]);

    const fileProperties = useGetRequest<FileProperty[]>({
        url: `application_operations/file_properties/`,
    });

    const [selectedFile, setSelectedFile] = useState<File | undefined | null>(
        undefined,
    );
    const wasFileUploadOrOperation = useMemo(
        () => hasFileSource(existingApplicationQuery.data),
        [existingApplicationQuery.data],
    );

    const tableReadOnly =
        !!existingApplicationId && wasFileUploadOrOperation;

    useEffect(() => {
        setValue(
            "_skipTableValidation",
            wasFileUploadOrOperation || !!selectedFile,
        );
    }, [wasFileUploadOrOperation, selectedFile, setValue]);

    useEffect(() => {
        if (selectedFile) {
            previousTableRef.current = getValues("table");
            setValue("table", []);
            trigger();
        } else if (!wasFileUploadOrOperation) {
            setValue("table", previousTableRef.current);
        }
    }, [selectedFile, wasFileUploadOrOperation, setValue, getValues, trigger]);

    return (
        <>
            <FormProvider {...methods}>
                <form>
                    <div className="flex flex-col gap-2 text-sm ">
                        <div className="md:flex md:flex-col md:gap-2 md:items-stretch">
                            <div className="flex flex-col gap-2 text-sm flex-1">
                                <DateInput
                                    name="start_date"
                                    label={t("date")}
                                    variant="flat"
                                    isRequired
                                    className="w-full"
                                    size="sm"
                                    labelPlacement="outside"
                                    showMonthAndYearPickers
                                />
                                <AutoCompleteInput
                                    options={rxOptions ?? []}
                                    label="Rx"
                                    name="prescription"
                                    size="sm"
                                    labelPlacement="outside"
                                    placeholder="Select Rx"
                                    isLoading={
                                        prescriptionQuery.isFetching ||
                                        selectedPrescriptionQuery.isFetching
                                    }
                                />
                            </div>
                            <TextAreaInput
                                className="flex-1"
                                name="note"
                                label={t("note")}
                                variant="flat"
                                size="sm"
                                labelPlacement="outside"
                                placeholder={t("note")}
                            />
                        </div>
                        <div className="md:flex md:flex-row md:gap-4 md:items-center w-full">
                            <SelectButtonInput
                                name="is_complete"
                                options={[
                                    {
                                        id: true,
                                        description: t("completeStatus"),
                                    },
                                    {
                                        id: false,
                                        description: t("pendingStatus"),
                                    },
                                ]}
                                orientation="horizontal"
                                label={t("status")}
                                isRequired
                                size="sm"
                                classNames={{
                                    base: "flex-1 w-full",
                                }}
                            />
                            <AutoCompleteInput
                                options={methodOptions ?? []}
                                label={t("method")}
                                name="method"
                                size="sm"
                                isRequired
                                labelPlacement="outside"
                                placeholder={t("method")}
                                className="flex-1"
                            />
                        </div>
                        <div className="md:flex md:flex-row md:gap-4 w-full md:justify-between">
                            {isFertigation && (
                                <AutoCompleteInput
                                    options={pumpOptions ?? []}
                                    label={t("pump")}
                                    name="pump"
                                    size="sm"
                                    labelPlacement="outside"
                                    placeholder={t("pump")}
                                />
                            )}
                            <ProductAutoComplete
                                selectedProductId={
                                    product != null ? String(product) : null
                                }
                                setSelectedProductId={(productId) => {
                                    if (
                                        productId != null &&
                                        productId !== ""
                                    ) {
                                        setValue(
                                            "product",
                                            Number(productId),
                                        );
                                        return;
                                    }
                                    const fallback =
                                        fieldQuery?.data?.default_product;
                                    if (fallback != null) {
                                        setValue("product", fallback);
                                    }
                                }}
                            />
                        </div>
                        <Divider />
                        {wasFileUploadOrOperation && (
                            <div className="text-red-500 text-sm italic">
                                This operation was uploaded or synced from a
                                connection
                            </div>
                        )}
                        {!isFertigation && !wasFileUploadOrOperation && (
                            <ShapefileUpload
                                fileInputProperties={fileProperties.data ?? []}
                                setFile={setSelectedFile}
                            />
                        )}
                        {!isFertigation &&
                            !selectedFile &&
                            !wasFileUploadOrOperation && (
                                <>
                                    <AutoCompleteInput
                                        options={managementZoneOptions ?? []}
                                        label={t("template")}
                                        name="template"
                                        size="sm"
                                        labelPlacement="outside"
                                        placeholder={t("template")}
                                    />
                                    <Divider />
                                </>
                            )}
                        {method && !selectedFile && (
                            <ApplicationTable
                                readOnly={tableReadOnly}
                                selectedProduct={selectedProduct}
                                loading={
                                    pumpsQuery.isFetching ||
                                    managementZonesQuery.isFetching ||
                                    irrigatedClipQuery.isFetching ||
                                    managementZoneQuery.isFetching ||
                                    selectedPrescriptionQuery.isFetching
                                }
                                irrigationSystem={
                                    fieldQuery?.data?.irrigation_systems[0]
                                }
                                hasSectors={hasSectors}
                            />
                        )}
                    </div>
                    {method && (
                        <div className="flex flex-row gap-2 justify-between mt-4 items-center">
                            <div className="text-red-500 text-sm">
                                {degreesErrorMessage}
                            </div>
                            <Button
                                color="secondary"
                                onPress={() => {
                                    trigger();
                                    handleSubmit(onSubmit)();
                                }}
                                isLoading={
                                    saveOperation.isPending ||
                                    uploadOperation.isPending
                                }
                            >
                                <FontAwesomeIcon
                                    icon={
                                        existingApplicationId
                                            ? faSave
                                            : faPlusCircle
                                    }
                                    className="mr-2"
                                />
                                {existingApplicationId
                                    ? t("saveOperation")
                                    : t("addOperation")}
                            </Button>
                        </div>
                    )}
                </form>
            </FormProvider>
        </>
    );
}

export default ApplicationOperationForm;
